
import { ActivityCodeApi } from "@/api/ActivityCodeApi";
import { CurrencyApi } from "@/api/CurrencyApi";
import { ProviderApi } from "@/api/ProviderApi";
import { BuyInvoiceFormData } from "@/formTypes/BuyInvoiceDataType";
import { FormConfigType } from "@/types/dynamicForm";
import { HIDDEN_INPUT_TYPE, PHONE_INPUT_TYPE, RADIO_INPUT_TYPE, SELECT_INPUT_TYPE, VALIDATION_ERROR_MESSAGE, Yup } from "@mavichpe/dynamic-forms";
import { ACTIVITY_CODE_LENGTH, INVOICE_CREDIT_TYPE_CODE, INVOICE_HOLDING_TYPE_CODE, INVOICE_TYPES, TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO, TIPOS_DE_IDENTIFICACION } from "@utils/Constants";
import { generateCurrencyOptionLabel } from "@utils/Currency";
import { withIdentificationFormat, withPhoneFormat } from "@utils/Forms";
import { CR_CANTONS, CR_DISTRICTS, CR_PROVINCES } from "@utils/LocationData";
import { CurrencyIdPubSub } from "@utils/pubsubs/PubSub";

const InvoiceDataFormConfig: FormConfigType = {
    sections: [
        {
            "fields": [
                [
                    {
                        "type": RADIO_INPUT_TYPE,
                        "name": "tipo",
                        "validation": Yup.string().required(),
                        "label": "Tipo Factura",
                        "isHorizontalValues": true,
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 6
                            }
                        },
                        "options": INVOICE_TYPES,
                        "defaultValue": 0,
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "currencyId",
                        "label": "Moneda",
                        "optionsFunction": () => CurrencyApi.optionsList({}, (i) => generateCurrencyOptionLabel(i)),
                        "onChangePubSub": (value: string) => CurrencyIdPubSub.publish({ currencyId: value }),
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": "number",
                        "isDecimal": true,
                        "name": "exchange_rate",
                        "min": "0",
                        "step": "0.01",
                        "label": "Tipo de Cambio",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3,
                            "xl": 2,
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "number",
                        "name": "plazo",
                        "min": "1",
                        "isHorizontal": true,
                        "suffix": "dias",
                        "visibleIf": {
                            "tipo": INVOICE_CREDIT_TYPE_CODE
                        },
                        "label": "Plazo",
                        "labelSizing": {
                            "sm": 2,
                            "md": 1,
                        },
                        "sizing": {
                            "sm": 4,
                            "md": 3,
                            "lg": 2,
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": "money",
                        "name": "monto_inicial",
                        "isHorizontal": true,
                        "min": "1",
                        "visibleIf": {
                            "tipo": INVOICE_HOLDING_TYPE_CODE
                        },
                        "label": "Abono Inicial",
                        "labelSizing": {
                            "sm": 2,
                            "md": 2,
                            "lg": 2,
                            "xl": 1,
                        },
                        "sizing": {
                            "sm": 4,
                            "md": 3,
                            "lg": 2,
                        }
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "reference_number",
                        "label": "Numero Referencia",
                        "visibilityHiddenIf": () => true, // by default this input is hidden, suscrption data make it visible
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 2,
                                "offset": 4
                            }
                        },
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "activitycode",
                        // Sin defaultValue: cualquier valor aquí se siembra en el form aunque sea 0
                        // (el select no lo muestra) y Yup lo da por válido al castearlo a "0".
                        "validation": Yup.string().required()
                            .length(ACTIVITY_CODE_LENGTH, "Se permite solo 6 caracteres para este codigo"),
                        "label": "Actividad Economica",
                        "optionsFunction": () => ActivityCodeApi.optionsList({}, undefined, "code"),
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 3
                            }
                        },
                    },
                    {
                        "type": "date",
                        "name": "fecha",
                        "label": "Fecha",
                        "sizing": {
                            "sm": 3
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ]
            ]
        },
        {
            title: "Informacion de Referencia",
            "fields": [
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "ref_doctype",
                        "label": "Tipo Referencia",
                        "options": {
                            "14": "Comprobante aportado por contribuyente de Régimen Especial.",
                            "16": "Comprobante de Proveedor No Domiciliado",
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR)
                    },
                    {
                        "type": "text",
                        "name": "ref_numero",
                        "label": "Numero Documento",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "ref_codigo",
                        "label": "Codigo",
                        "options": {
                            "04": "Referencia a otro documento",
                            "08": "Factura Endosada",
                            "11": "Proveedor No Domiciliado",
                        },
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": "date",
                        "name": "ref_date",
                        "label": "Fecha Documento",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "ref_razon",
                        "label": "Razon Referencia",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ]
            ]
        },
        {
            title: "Datos de Provedor",
            "fields": [
                [
                    {
                        "type": "text",
                        "name": "provider_code",
                        "exludeFromSubmit": true,
                        "label": "Cod",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 2
                            }
                        }
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "providerId",
                        "label": "Provedor",
                        "optionsFunction": () => ProviderApi.optionsList(),
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 9
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    }
                ],
                [
                    {
                        "type": "text",
                        "name": "nombre_factura",
                        "label": "Nombre Provedor",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 12
                            }
                        }
                    },
                ],
                [
                    {
                        // La razon social se guarda aparte del nombre de facturacion porque
                        // Hacienda espera el nombre legal en Receptor/Nombre y el comercial
                        // en Receptor/NombreComercial. No se edita: sale de la ficha maestra.
                        "type": HIDDEN_INPUT_TYPE,
                        "name": "razon_social",
                    },
                ],
                [
                    {
                        "type": PHONE_INPUT_TYPE,
                        "name": "tel1",
                        "label": "Telefono 1",
                        "validation": withPhoneFormat(Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR))
                    },
                    {
                        "type": PHONE_INPUT_TYPE,
                        "name": "tel2",
                        "label": "Telefono 2",
                        "validation": withPhoneFormat(Yup.string().notRequired()),
                    },
                    {
                        "type": "text",
                        "name": "email",
                        "label": "Email",
                        "validation": Yup.string().required("El campo de email es obligatorio")
                            .test("is-valid-emails", "Uno o más correos no son válidos", (value) => {
                                if (!value) return false;

                                // Separar por coma y limpiar espacios
                                const emails = value.split(",").map((email) => email.trim());

                                // Validar cada correo usando una expresión regular o Yup
                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                return emails.every((email) => emailRegex.test(email));
                            })
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "tipo_identificacion",
                        "label": "Tipo Identificacion",
                        "options": TIPOS_DE_IDENTIFICACION,
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR)
                    },
                    {
                        "type": "text",
                        "name": "identificacion",
                        "label": "Numero Identificacion",
                        "sizing": {
                            "sm": 4
                        },
                        "validation": withIdentificationFormat(Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR))
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "clientActivitycode",
                        "label": "Codigo Actividad Economica",
                        "optionsFunction": () => ActivityCodeApi.optionsList({}, undefined, "code"),
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR)
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "province_id",
                        "label": "Provincia",
                        "options": CR_PROVINCES,
                        "validation": Yup.string().when("tipo_identificacion", {
                            is: (tipo_identificacion: unknown) => tipo_identificacion != TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO,
                            then: (s) => s.required("Es obligatorio la informacion de Provincia del comercio donde se realizo la compra"),
                            otherwise: (s) => s.notRequired(),
                        })
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "canton_id",
                        "label": "Canton",
                        "reFetchAfter": ["province_id"],
                        "optionsFunction": (formdata: BuyInvoiceFormData) => {
                            if (formdata["province_id"]) {
                                return CR_CANTONS
                                    .filter(c => c.province_id == formdata["province_id"])
                                    .reduce((a, c) => {
                                        return {
                                            ...a,
                                            [c.canton_id]: c.name
                                        }

                                    }, {});
                            }
                            return {}
                        },
                        "validation": Yup.string().when("tipo_identificacion", {
                            is: (tipo_identificacion: unknown) => tipo_identificacion != TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO,
                            then: (s) => s.required("Es obligatorio la informacion del Canton del comercio donde se realizo la compra"),
                            otherwise: (s) => s.notRequired(),
                        })
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "district_id",
                        "label": "Distrito",
                        "reFetchAfter": ["province_id", "canton_id"],
                        "optionsFunction": (formdata: BuyInvoiceFormData) => {
                            if (formdata["canton_id"]) {
                                return CR_DISTRICTS
                                    .filter(d => d.canton_id == formdata["canton_id"])
                                    .reduce((a, c) => {
                                        return {
                                            ...a,
                                            [c.district_id]: c.name
                                        }
                                    }, {});
                            }
                            return {}
                        },
                        "validation": Yup.string().when("tipo_identificacion", {
                            is: (tipo_identificacion: unknown) => tipo_identificacion != TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO,
                            then: (s) => s.required("Es obligatorio la informacion del distrito del comercio donde se realizo la compra"),
                            otherwise: (s) => s.notRequired(),
                        })
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "direccion",
                        "label": "Dirección",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 12
                            }
                        },
                        "validation": Yup.string().when("tipo_identificacion", {
                            is: (tipo_identificacion: unknown) => tipo_identificacion == TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO,
                            then: (s) => s.required("Para extranjeros no domiciliados, es obligatorio la informacion de la direcciona")
                                .min(5, "Es requerido al menos 5 caracteres para la direccion")
                                .max(300, "El tamaño maximo para la direccion de 300 caracteres, por favor reduzca el tamaño de la direccion"),
                            otherwise: (s) => s.notRequired(),
                        })
                    },
                ],
            ]
        }
    ],
};


export default InvoiceDataFormConfig;